import {find, noop, stubTrue} from 'lodash';
import React, {FC} from 'react';

import {Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {SendActionsEnum} from '../../../../../libs/shared/core/src/models/api/messageModel';
import {OnboardingDataEventObject} from '../../../../../libs/shared/core/src/models/tracking/objectNames/onboardingDataEventObject';
import {getDefaultCellsContext} from '../../../../../libs/shared/core/src/store/panels/cells/context/cellsContext';
import {ViewerPanelId} from '../../../../../libs/shared/core/src/store/panels/panelStateBase';
import {InfoBubblePillVariantsEnum} from '../../../../../libs/shared/ui/src/components/tip/infoBubblePill';
import {AppParametricSelector, createSelector} from '../../../../../srcCommon/helpers/reselectHelpers';
import {useAppDispatch, useAppSelector} from '../../../../helpers/appReduxHelpers';
import {delayAsync} from '../../../../helpers/asyncHelpers';
import {buildCommentUrlBuilder, buildDraftComment} from '../../../../helpers/models/commentHelpers';
import {CommentStatusesEnum} from '../../../../models/api/commentModel';
import {ConversationModel} from '../../../../models/api/conversationModel';
import {ThunkAsyncAction} from '../../../../store/appThunk';
import {saveCommentAsyncAction} from '../../../../store/data/comments/saveCommentActions';
import {sendCommentAsyncAction} from '../../../../store/data/comments/sendCommentAction';
import {
  makeGetConversation,
  makeGetDraftComments,
} from '../../../../store/data/conversations/conversationsSelectors';
import {getIsSandboxGeneralInboxTicketingEnabled} from '../../../../store/data/inboxes/inboxesSelectors';
import {getUser, getUserId} from '../../../../store/data/teammates/teammatesSelectors';
import {EducationTip} from '../../../educationTips/educationTip';
import {generateStepText} from '../../welcome/onboardingWelcomeHelpers';
import {SandboxEducationTipProps} from './sandboxEducationTip';

interface MentionEducationTipProps extends SandboxEducationTipProps {
  panelId: ViewerPanelId;
  conversationId: number | undefined;
  onSetCommentComposerText: (newText: string) => void;
}

/*
 * Constants.
 */

const mentionText = '@marisa_demo can you help?';

/*
 * Component.
 */

export const MentionEducationTip: FC<MentionEducationTipProps> = (props) => {
  const {conversationId, panelId, onSetCommentComposerText} = props;

  const dispatch = useAppDispatch();

  const sendMention = async () => {
    if (!conversationId) {
      return;
    }

    await dispatch(sendMentionAsyncAction(conversationId, panelId, onSetCommentComposerText));
  };

  const hasTicketingSupportInbox = useAppSelector(getIsSandboxGeneralInboxTicketingEnabled);

  return (
    <EducationTip
      anchor={props.anchor}
      position="top"
      isVisible={props.isVisible}
      isStandalone
      onRequestClose={noop}
      pillType={InfoBubblePillVariantsEnum.EDUCATION}
      title={<Trans id="infobubble-onboarding-mention-title">Loop in a teammate with an @mention</Trans>}
      stepCounter={generateStepText(hasTicketingSupportInbox ? 3 : 2, hasTicketingSupportInbox ? 4 : 2)}
      dataObjectName={OnboardingDataEventObject.DEMO_MENTION}
      primaryActionLabel={<Trans id="infobubble-onboarding-mention-primary-action-label">Next</Trans>}
      onPrimaryClick={sendMention}
    >
      <Trans id="infobubble-onboarding-mention-description">
        Use comments to collaborate with your team behind the scenes. Give it a try by typing the following
        into the comment bar:
        <br />
        <em>&ldquo;{mentionText}&rdquo;</em>
      </Trans>
    </EducationTip>
  );
};

/*
 * Helpers.
 */

function makeGetComment(getConversation: AppParametricSelector<undefined, ConversationModel | undefined>) {
  return createSelector([getUserId, makeGetDraftComments(getConversation)], (userId, draftComments) =>
    find(
      draftComments,
      (draftComment) => draftComment.authorId === userId && draftComment.status === CommentStatusesEnum.DRAFT,
    ),
  );
}

function sendMentionAsyncAction(
  conversationId: number,
  panelId: ViewerPanelId,
  onSetCommentComposerText: (newText: string) => void,
): ThunkAsyncAction<void> {
  return async (dispatch, getState) => {
    const state = getState();
    const conversation = makeGetConversation(() => conversationId)(state, {});
    const maybeComment = makeGetComment(makeGetConversation(() => conversationId))(state);
    const user = getUser(state);

    if (!conversation) {
      return;
    }

    const comment =
      maybeComment ??
      buildDraftComment({
        urlBuilder: buildCommentUrlBuilder(conversation.url),
        authorId: user.id,
        conversationId,
        lastPanelId: panelId,
      });

    // For loop adds typing animation effect.
    for (let i = 0; i < mentionText.length + 1; i++) {
      const newText = mentionText.slice(0, i);
      onSetCommentComposerText(newText);
      const jitter = Math.random() * (3 - 1) + 1;
      await delayAsync(40 * jitter);
    }

    await dispatch(saveCommentAsyncAction(comment, {newText: mentionText, panelId}));

    const cellsContext = getDefaultCellsContext(state, stubTrue);

    await dispatch(
      sendCommentAsyncAction({
        conversationId,
        cellsContext,
        commentUid: comment.uid,
        sendAction: SendActionsEnum.SEND,
      }),
    );
  };
}
